import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { ThemeProps, SelectableThemeProps } from '@theme/base';
import { BaseStyledTextarea } from '@styles/common';
import { Pill } from '@components/search/destinations/suggested-destinations/Pill';
import { isTablet, isMobile, mq } from '@styles/breakpoints';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { Icons } from '@model/common';
import { Icon } from '@components/common/generic-icon';
import { TourHit } from '@model/search/alogolia/tour-hit';
import { isSingleTour } from '@util/tours';

const SelectionContainer: any = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group2.lighter,
  paddingLeft: theme.custom.spacing.medium,
  paddingRight: theme.custom.spacing.medium,
  paddingTop: theme.custom.spacing.medium,
  [mq.medium]: {
    width: '100%'
  }
}));

const InputContainer: any = styled.div(({ theme, isSelected }: SelectableThemeProps) => ({
  background: theme.custom.colors.white,
  border: `1px solid ${isSelected ? theme.custom.colors.group1.base : theme.custom.colors.group10.light}`,
  borderRadius: 10,
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 0%',
  flexWrap: 'wrap',
  padding: theme.custom.spacing.small
}));

const SearchIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.8rem',
  padding: `0 ${theme.custom.spacing.small}px`
}));

const StyledInput = styled(BaseStyledTextarea)({});

const StyledPill = styled(Pill)({
  margin: 2,
  height: 'fit-content'
});

const Separator: any = styled.hr(({ theme }: ThemeProps) => ({
  border: 'none',
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  margin: `${theme.custom.spacing.medium}px -${theme.custom.spacing.medium}px ${theme.custom.spacing.medium}px`,
  marginBottom: 0
}));

interface HeadingProps {
  input: string;
  values: Array<TourHit>;
  onChange: (payload: string) => void;
  onRemove: (payload: TourHit) => void;
  placeholderKey?: string;
}

export const Heading = ({ input, values, onChange, onRemove, placeholderKey }: HeadingProps) => {
  const ref: any = useRef();
  const t: any = useI18NextContext();
  const handleOnChange = (event: any) => onChange(event.target.value);
  const isTourSearch: boolean = values.length === 1 && isSingleTour(values[0]);

  useEffect(() => {
    if (!isTablet() && !isMobile()) {
      ref.current?.focus();
    }
  }, [values]);

  return (
    <SelectionContainer data-testid={TestId.tours.search.heading}>
      <InputContainer onClick={() => ref.current?.focus()}>
        <SearchIcon name={Icons.SEARCH} />
        {values.map((option) => (
          <StyledPill key={option.name} display={option.name} value={option} onClear={onRemove} />
        ))}
        {!isTourSearch && (
          <StyledInput
            ref={ref}
            value={input}
            onChange={handleOnChange}
            placeholder={t(placeholderKey || 'tours-search__destination--placeholder')}
          />
        )}
      </InputContainer>
      <Separator />
    </SelectionContainer>
  );
};
